import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Plus, Loader2, CheckCircle, Clock, Edit, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { VersionEditor } from './NocodeVersionEditor';
import CreateVersionModal from './CreateVersionModal';

export const SimulatorDetailModal = ({ simulator, onClose, token, lang, permissions, userRole }) => {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateVersion, setShowCreateVersion] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [simulator.id]);

  const fetchVersions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/simulators/${simulator.id}/versions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
        if (data.versions?.length > 0) {
          setSelectedVersion(data.versions[0]);
        }
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'published': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'submitted': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <Edit className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-xl font-bold">{simulator.name}</h2>
            <p className="text-sm text-gray-500 font-mono">{simulator.key}</p>
          </div>
          <Button variant="ghost" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          </div>
        ) : (
          <div className="flex flex-1 overflow-hidden">
            {/* Versions list */}
            <div className="w-64 border-r bg-gray-50 overflow-y-auto flex flex-col">
              <div className="p-3 border-b">
                <Button 
                  size="sm" 
                  className="w-full"
                  onClick={() => setShowCreateVersion(true)}
                >
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle version
                </Button>
              </div>
              <div className="p-2 space-y-1 flex-1 overflow-y-auto">
                {versions.map(v => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVersion(v)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedVersion?.id === v.id
                        ? 'bg-blue-100 text-blue-800'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getStatusIcon(v.status)}
                      <span className="font-medium text-sm">{v.version_number}</span>
                    </div>
                    <p className="text-xs text-gray-500 ml-6 truncate">{v.name}</p>
                    <p className="text-xs text-gray-400 ml-6">{v.rules_count || 0} règles</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Version detail */}
            {selectedVersion && (
              <VersionEditor 
                version={selectedVersion}
                simulatorId={simulator.id}
                token={token}
                lang={lang}
                permissions={permissions}
                userRole={userRole}
                onUpdate={fetchVersions}
              />
            )}
          </div>
        )}
      </div>

      {/* Create Version Modal */}
      {showCreateVersion && (
        <CreateVersionModal
          simulatorId={simulator.id}
          existingVersions={versions}
          token={token}
          onSuccess={(newVersion) => {
            setShowCreateVersion(false);
            fetchVersions();
            setSelectedVersion(newVersion);
          }}
          onCancel={() => setShowCreateVersion(false)}
          lang={lang}
        />
      )}
    </div>
  );
};

// Version Editor Component
